import { AfterViewInit, Component, ElementRef, EventEmitter, forwardRef, Input, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { sanitizeHtml } from '../../../utils/sanitize-html.util';

/** The formats the toolbar can offer, in the order Quill's snow toolbar rendered them. */
export type RichTextFormat = 'bold' | 'italic' | 'underline' | 'link' | 'ordered' | 'bullet';

/**
 * execCommand ids for the formats that map 1:1. `link` needs a URL, so it is handled separately.
 *
 * `insertUnorderedList` is the whole point of the list pair: Quill 2 serialised BOTH list kinds as
 * `<ol><li data-list="bullet|ordered">`, and `data-list` is not in the sanitiser's attribute
 * allow-list, so every bullet list silently became a numbered one on save. execCommand emits a
 * real `<ul>`, which the allow-list already permits.
 */
const EXEC_COMMANDS: Record<Exclude<RichTextFormat, 'link'>, string> = {
    bold: 'bold',
    italic: 'italic',
    underline: 'underline',
    ordered: 'insertOrderedList',
    bullet: 'insertUnorderedList'
};

/** Everything a browser leaves behind for "the user deleted all of the content". */
const BLANK_HTML = new Set(['', '<br>', '<p></p>', '<div></div>', '<p><br></p>', '<div><br></div>']);

/** http/https/mailto, plus root-relative paths and anchors — mirrors ALLOWED_URI_REGEXP's intent. */
const SAFE_LINK = /^(?:https?:|mailto:|[/#])/i;

const HAS_SCHEME = /^[a-z][a-z0-9+.\-]*:/i;

/**
 * The editing surface behind `<ft-text-html-editor>`, replacing `<quill-editor>` (FTK-7505).
 *
 * quill <= 2.0.3 carries GHSA-v3m3-f69x-jf25 / CVE-2025-15056 (XSS via `getSemanticHTML()`) and
 * has no patched release, so the library had to go rather than be bumped. What replaces it is a
 * `contenteditable` div driven by `document.execCommand`, which is deliberately modest: the
 * toolbar only ever exposed bold/italic/underline/link/ordered/bullet, and
 * `sanitizeHtmlOpeningLinksInNewWindow` — not the editor — has always been the output contract.
 *
 * Three long-standing bugs go with Quill:
 *
 * 1. `getSemanticHTML()` hardcodes `escapedText.replaceAll(' ', '&nbsp;')`, so opening and saving
 *    any record rewrote every space to a non-breaking one (0 of 400 stored values survived a no-op
 *    save byte-identical). Reading `innerHTML` has nothing to undo.
 * 2. Bullet lists became numbered ones — see EXEC_COMMANDS above.
 * 3. The plain-text clipboard was registered GLOBALLY and one-way (`Quill.register('modules/
 *    clipboard', ...)`), so the first editor with `trimClipboardFormat` changed paste behaviour for
 *    every editor in the app until reload. `plainClipboard` here is a per-instance input.
 *
 * The `.ql-editor` / `.ql-container` names are kept on purpose: the overrides in
 * `libs/shared-assets/styles/elements/_editors.scss` size the editing area through them, and
 * keeping the names keeps that layout byte-for-byte.
 *
 * The toolbar is NOT here — `ft-text-html-editor` renders one for the whole field group and calls
 * `applyFormat` / `openLinkEditor` on whichever editor is focused. This component still owns every
 * command and the popover's state, because both work off the range it caches.
 */
@Component({
    selector: 'ft-rich-text-editor',
    templateUrl: './rich-text-editor.component.html',
    styleUrls: ['./rich-text-editor.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => RichTextEditorComponent)
        }
    ],
    standalone: false
})
export class RichTextEditorComponent implements ControlValueAccessor, AfterViewInit {
    @ViewChild('editable', { static: true }) editableRef: ElementRef<HTMLElement>;

    @Input() placeholder = '';
    /** Paste drops all formatting on THIS instance only. */
    @Input() plainClipboard = false;

    @Output() editorFocus = new EventEmitter<void>();
    @Output() editorBlur = new EventEmitter<void>();
    @Output() editorCreated = new EventEmitter<RichTextEditorComponent>();

    isBlank = true;
    disabled = false;
    activeFormats: Partial<Record<RichTextFormat, boolean>> = {};
    /**
     * The inline `.ql-tooltip` link popover, which replaces Quill's own. The STATE is here because
     * every action on it (prefill, retarget, unlink) works off this editor's saved range; the markup
     * is rendered by `ft-text-html-editor` alongside the shared toolbar, which is also what focuses
     * the input once this flag flips.
     */
    linkEditorOpen = false;
    linkDraft = '';

    private value = '';
    private applied = false;
    private savedRange: Range | null = null;
    private onChange: (value: string) => void = () => undefined;
    private onTouched: () => void = () => undefined;

    ngAfterViewInit() {
        this.configureCommands();

        if (!this.applied) {
            this.applyHtml(this.value);
        }
        this.editorCreated.emit(this);
    }

    writeValue(value: string): void {
        const next = value ?? '';
        if (next === this.value) {
            return;
        }
        this.value = next;
        this.applyHtml(next);
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    applyFormat(format: RichTextFormat) {
        if (format === 'link') {
            this.openLinkEditor();
            return;
        }

        this.focusEditable();
        this.configureCommands();

        if (format === 'ordered' || format === 'bullet') {
            this.applyList(format);
        } else {
            this.exec(EXEC_COMMANDS[format]);
        }

        this.syncSelection();
        this.emitValue();
    }

    /**
     * Opens the inline `.ql-tooltip` popover, prefilled with the link under the caret if there is
     * one. The host focuses its input, which collapses the document selection — harmless, because
     * `syncSelection` has already cached the range and every action below restores it first.
     */
    openLinkEditor() {
        this.syncSelection();
        this.linkDraft = this.closestLink()?.getAttribute('href') || '';
        this.linkEditorOpen = true;
    }

    closeLinkEditor() {
        this.linkEditorOpen = false;
        this.focusEditable(true);
    }

    commitLink(url: string) {
        this.linkEditorOpen = false;
        this.focusEditable(true);
        this.configureCommands();
        this.applyLink(url);
        this.syncSelection();
        this.emitValue();
    }

    removeLink() {
        this.linkEditorOpen = false;
        this.focusEditable(true);
        this.configureCommands();
        this.selectLinkUnderCaret();
        this.exec('unlink');
        this.syncSelection();
        this.emitValue();
    }

    /** Inserts text at the caret and leaves it selected, as Quill's token insertion did. */
    insertText(text: string) {
        if (!text) {
            return;
        }
        this.focusEditable();
        this.configureCommands();
        this.exec('insertText', text);
        this.selectPrecedingText(text.length);
        this.syncSelection();
        this.emitValue();
    }

    focus() {
        this.editableRef?.nativeElement?.focus();
    }

    onInput() {
        this.isBlank = this.isEditorBlank();
        this.syncSelection();
        this.emitValue();
    }

    onPaste(event: ClipboardEvent) {
        const clipboard = event.clipboardData;
        if (!clipboard) {
            return;
        }
        event.preventDefault();
        this.configureCommands();

        const html = this.plainClipboard ? '' : clipboard.getData('text/html');
        if (html) {
            // Same sanitiser the save path runs, so what the author sees pasted is what is stored
            // — Quill's clipboard did the equivalent by normalising paste through its format
            // registry, and without it a paste from Word or Gmail lands as raw styled markup.
            this.exec('insertHTML', sanitizeHtml(html));
        } else {
            this.exec('insertText', clipboard.getData('text/plain'));
        }

        this.isBlank = this.isEditorBlank();
        this.syncSelection();
        this.emitValue();
    }

    onEditableFocus() {
        this.syncSelection();
        this.editorFocus.emit();
    }

    onEditableBlur() {
        this.onTouched();
        // While the link popover is open this editor is still the active one — focus has only moved
        // to the popover's own input. Emitting blur would null the host's `activeEditorIndex`, which
        // both dims the shared toolbar (`pointer-events: none`) and detaches the popover from the
        // editor it is editing — swallowing its own Apply/Remove clicks.
        if (!this.linkEditorOpen) {
            this.editorBlur.emit();
        }
    }

    /**
     * Remembers where the caret is. Bound to the editor's own events rather than a document-level
     * `selectionchange` listener, which would run change detection on every caret move app-wide.
     */
    syncSelection() {
        const editable = this.editableRef?.nativeElement;
        const selection = this.getSelection();
        if (editable && selection?.rangeCount) {
            const range = selection.getRangeAt(0);
            if (editable.contains(range.commonAncestorContainer)) {
                this.savedRange = range.cloneRange();
            }
        }
        this.refreshActiveFormats();
    }

    private applyHtml(html: string) {
        const editable = this.editableRef?.nativeElement;
        if (!editable) {
            return;
        }
        // Replaces ngx-quill's `[sanitize]="true"`. It matters most on the incentives redeem
        // instructions, which wrote straight to the model for years without any save-time
        // sanitiser, so rows predating FTK-7505 can hold markup the allow-list rejects.
        editable.innerHTML = sanitizeHtml(html);
        this.applied = true;
        this.isBlank = this.isEditorBlank();
    }

    private emitValue() {
        const next = this.readHtml();
        if (next === this.value) {
            return;
        }
        this.value = next;
        this.onChange(next);
    }

    private readHtml(): string {
        const editable = this.editableRef?.nativeElement;
        if (!editable) {
            return '';
        }
        const html = editable.innerHTML;
        return BLANK_HTML.has(html) ? '' : html;
    }

    private isEditorBlank(): boolean {
        const editable = this.editableRef?.nativeElement;
        return !editable || BLANK_HTML.has(editable.innerHTML);
    }

    /**
     * The list commands need two repairs Chrome does not make itself, both verified in the browser
     * against the incentives Terms & Conditions editor:
     *
     * 1. `insertOrderedList`/`insertUnorderedList` collapse the caret to offset 0 of the new item,
     *    whatever it was before. Left alone, typing after toggling a list runs BACKWARDS —
     *    "Late checkout" typed after "Breakfast for two" lands as "Late checkoutBreakfast for two".
     *    The commands never change the text, so the caret is saved and restored by character offset.
     * 2. Toggling a list on a paragraph leaves the new list INSIDE it (`<p><ul>…</ul></p>`). That is
     *    invalid nesting, and while it survives sanitisation it does not survive re-parsing: the
     *    HTML parser hoists the list out on the next load, so a no-op open-and-save would rewrite
     *    the stored value. Hoisting it here keeps the round-trip byte-identical.
     */
    private applyList(format: 'ordered' | 'bullet') {
        const caret = this.captureCaret();
        this.exec(EXEC_COMMANDS[format]);
        this.hoistListsOutOfParagraphs();
        this.restoreCaret(caret);
    }

    private hoistListsOutOfParagraphs() {
        const editable = this.editableRef?.nativeElement;
        editable?.querySelectorAll('p > ul, p > ol').forEach(list => {
            const paragraph = list.parentElement;
            paragraph.parentElement?.insertBefore(list, paragraph.nextSibling);
            if (!paragraph.textContent && !paragraph.querySelector('img')) {
                paragraph.remove();
            }
        });
    }

    /** The selection as character offsets into the editor's text, which survive a re-shaped DOM. */
    private captureCaret(): { start: number; end: number } | null {
        const editable = this.editableRef?.nativeElement;
        const selection = this.getSelection();
        if (!editable || !selection?.rangeCount) {
            return null;
        }
        const range = selection.getRangeAt(0);
        if (!editable.contains(range.commonAncestorContainer)) {
            return null;
        }
        const toStart = range.cloneRange();
        toStart.selectNodeContents(editable);
        toStart.setEnd(range.startContainer, range.startOffset);
        const start = toStart.toString().length;
        return { start, end: start + range.toString().length };
    }

    private restoreCaret(caret: { start: number; end: number } | null) {
        const editable = this.editableRef?.nativeElement;
        const selection = this.getSelection();
        if (!editable || !selection || !caret) {
            return;
        }

        const range = document.createRange();
        range.selectNodeContents(editable);
        range.collapse(false);

        const walker = document.createTreeWalker(editable, NodeFilter.SHOW_TEXT);
        let consumed = 0;
        let node = walker.nextNode();
        let startPlaced = false;
        while (node) {
            const length = node.textContent?.length ?? 0;
            if (!startPlaced && caret.start <= consumed + length) {
                range.setStart(node, caret.start - consumed);
                startPlaced = true;
            }
            if (startPlaced && caret.end <= consumed + length) {
                range.setEnd(node, caret.end - consumed);
                break;
            }
            consumed += length;
            node = walker.nextNode();
        }

        selection.removeAllRanges();
        selection.addRange(range);
    }

    /**
     * Re-asserted before every command rather than once at init: both are document-wide toggles that
     * any other editing surface on the page can flip.
     *
     * `styleWithCSS=false` keeps the markup tag-based (`<b>`, not `<span style="font-weight:bold">`)
     * — `b`/`i`/`u` are in the sanitiser's allow-list and `style` deliberately is not, so a
     * CSS-styled span would be flattened to unformatted text on the next save.
     * `defaultParagraphSeparator=p` makes Enter open a `<p>` rather than Chrome's `<div>`, matching
     * what Quill stored.
     */
    private configureCommands() {
        this.exec('styleWithCSS', 'false');
        this.exec('defaultParagraphSeparator', 'p');
    }

    private applyLink(answer: string) {
        const url = this.normalizeLink(answer);
        if (!url) {
            this.selectLinkUnderCaret();
            this.exec('unlink');
            return;
        }

        // execCommand('createLink') is a no-op on a collapsed selection, where Quill inserted the
        // URL as its own link text. Insert it, select it back, then link it — but only when the
        // caret is not already inside a link, which `selectLinkUnderCaret` handles instead.
        if (!this.selectLinkUnderCaret() && this.getSelection()?.isCollapsed) {
            this.exec('insertText', url);
            this.selectPrecedingText(url.length);
        }
        this.exec('createLink', url);
    }

    /**
     * With a collapsed caret inside an existing link, selects the whole anchor and reports it.
     *
     * Both commands need this and neither does it themselves: `createLink` would SPLIT the anchor,
     * dropping the new URL in as text mid-word (`<a>ful</a><a>https://…</a><a>l terms</a>`), and
     * `unlink` on a collapsed caret does nothing at all. Only for a collapsed caret — an expanded
     * selection is what the author actually chose, links inside it included.
     */
    private selectLinkUnderCaret(): boolean {
        const selection = this.getSelection();
        if (!selection?.isCollapsed) {
            return false;
        }
        const anchor = this.closestLink();
        if (!anchor) {
            return false;
        }
        const range = document.createRange();
        range.selectNodeContents(anchor);
        selection.removeAllRanges();
        selection.addRange(range);
        return true;
    }

    /**
     * Empty clears the link. A disallowed scheme (`javascript:`, `data:`) is dropped rather than
     * written and left for the sanitiser. Anything without a scheme is assumed to be https.
     */
    private normalizeLink(answer: string): string | null {
        const url = answer.trim();
        if (!url) {
            return null;
        }
        if (SAFE_LINK.test(url)) {
            return url;
        }
        return HAS_SCHEME.test(url) ? null : `https://${url}`;
    }

    private closestLink(): HTMLAnchorElement | null {
        const editable = this.editableRef?.nativeElement;
        const node = this.getSelection()?.focusNode;
        if (!editable || !node || !editable.contains(node)) {
            return null;
        }
        const start = node.nodeType === Node.ELEMENT_NODE ? node as Element : node.parentElement;
        const anchor = start?.closest('a');
        return anchor && editable.contains(anchor) ? anchor as HTMLAnchorElement : null;
    }

    /**
     * `force` is for the link popover: focus is on its own input, and the caret has to be put back
     * before `createLink`/`unlink` can act on anything.
     */
    private focusEditable(force = false) {
        const editable = this.editableRef?.nativeElement;
        if (!editable || (!force && document.activeElement === editable)) {
            return;
        }
        // `focus()` fires the editor's own `focus` handler, which calls `syncSelection()` — and at
        // that instant the selection is Chrome's post-focus caret, so it would overwrite the very
        // range being restored here. Symptom in the browser: applying a link to a selection put the
        // URL in as text at position 0 instead of wrapping the selected words. Hold the range across
        // the focus call.
        const range = this.savedRange;
        editable.focus();
        this.savedRange = range;
        this.restoreSelection();
    }

    private restoreSelection() {
        const editable = this.editableRef?.nativeElement;
        const selection = this.getSelection();
        if (!editable || !selection) {
            return;
        }

        const range = document.createRange();
        if (this.savedRange && editable.contains(this.savedRange.commonAncestorContainer)) {
            selection.removeAllRanges();
            selection.addRange(this.savedRange);
            return;
        }

        // Quill's `selection ? selection.index : 0` fell back to the very start; keep that.
        range.selectNodeContents(editable);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
    }

    private selectPrecedingText(length: number) {
        const selection = this.getSelection();
        const node = selection?.focusNode;
        if (!selection || !node || node.nodeType !== Node.TEXT_NODE) {
            return;
        }
        const end = selection.focusOffset;
        if (end < length) {
            return;
        }
        const range = document.createRange();
        range.setStart(node, end - length);
        range.setEnd(node, end);
        selection.removeAllRanges();
        selection.addRange(range);
    }

    private refreshActiveFormats() {
        this.activeFormats = {
            bold: this.queryState('bold'),
            italic: this.queryState('italic'),
            underline: this.queryState('underline'),
            ordered: this.queryState('insertOrderedList'),
            bullet: this.queryState('insertUnorderedList'),
            link: !!this.closestLink()
        };
    }

    private queryState(command: string): boolean {
        try {
            return typeof document.queryCommandState === 'function' && document.queryCommandState(command);
        } catch {
            return false;
        }
    }

    private getSelection(): Selection | null {
        return typeof document.getSelection === 'function' ? document.getSelection() : null;
    }

    /** `execCommand` is deprecated but is still the only cross-browser rich-text primitive. */
    private exec(command: string, value?: string): boolean {
        if (typeof document.execCommand !== 'function') {
            return false;
        }
        try {
            return document.execCommand(command, false, value);
        } catch {
            return false;
        }
    }
}
